import React, { useState, useEffect } from "react";
import AppModal from "@/components/ui/AppModal";
import { FAMILY_RELATIONSHIPS, PHONE_COUNTRIES } from '@/util/dropdowns';
import { gql } from "@apollo/client";
import { apolloClient } from "@/lib/apollo/client";

interface Props {
  isOpen: boolean;
  onClose: (created?: { id: string; label: string }) => void;
}

const CREATE_FAMILY_REL = gql`
  mutation createFamilyRelationship($data: CreateFamilyRelationshipInput!) {
    createFamilyRelationship(data: $data)
  }
`;

const CHECK_USER_AVAILABLE = gql`
  query checkUserAvailable($data: CheckUserInput!) {
    checkUserAvailable(data: $data)
  }
`;

const USER_VERIFICATION = gql`
  mutation userVerification($data: UserVerification!) {
    userVerification(data: $data)
  }
`;

export default function NewFamilyMemberModal({ isOpen, onClose }: Props) {
  const isClubleaDomain = () => {
    try {
      return window.location.hostname.includes('clublea');
    } catch (e) {
      return false;
    }
  };
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [relationshipToMe, setRelationshipToMe] = useState("");
  const [relationshipToThem, setRelationshipToThem] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("+44");
  const [dob, setDob] = useState("");
  const [child, setChild] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [newAccount, setNewAccount] = useState(false);
  const [gender, setGender] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [createdInfo, setCreatedInfo] = useState<{ id: string; label: string } | null>(null);
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");

  useEffect(() => {
    if (!showVerificationModal || !createdInfo) return;
    let mounted = true;
    const sendCodes = async () => {
      try {
        // send email code
        await apolloClient.mutate({
          mutation: USER_VERIFICATION,
          variables: { data: { userID: createdInfo.id, verificationType: 'EMAIL', sendCode: true, verificationCode: '' } },
        });
        // send phone code for adults
        if (isAdult) {
          await apolloClient.mutate({
            mutation: USER_VERIFICATION,
            variables: { data: { userID: createdInfo.id, verificationType: 'PHONE', sendCode: true, verificationCode: '' } },
          });
        }
      } catch (err: any) {
        console.error('failed to send verification codes', err);
        if (mounted) setError('Failed to send verification codes');
      }
    };
    sendCodes();
    return () => { mounted = false; };
  }, [showVerificationModal, createdInfo, isAdult]);

  const handleVerify = async () => {
    if (!createdInfo) return;
    setError(null);
    setLoading(true);
    try {
      // verify email
      const resEmail: any = await apolloClient.mutate({
        mutation: USER_VERIFICATION,
        variables: { data: { userID: createdInfo.id, verificationType: 'EMAIL', sendCode: false, verificationCode: emailCode } },
      });
      const okEmail = resEmail?.data?.userVerification?.success ?? false;
      if (!okEmail) throw new Error('Email verification failed');

      if (isAdult) {
        const resPhone: any = await apolloClient.mutate({
          mutation: USER_VERIFICATION,
          variables: { data: { userID: createdInfo.id, verificationType: 'PHONE', sendCode: false, verificationCode: phoneCode } },
        });
        const okPhone = resPhone?.data?.userVerification?.success ?? false;
        if (!okPhone) throw new Error('Phone verification failed');
      }

      setShowVerificationModal(false);
      setSuccessMessage(`${createdInfo.label} verified successfully`);
      onClose(createdInfo);
    } catch (err: any) {
      console.error('verification failed', err);
      setError(err?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { fontSize: '12px' } as React.CSSProperties;

  const normalizePhone = (p: string) => {
    if (!p) return p;
    if (p.startsWith("+")) return p;
    const cleaned = p.replace(/[^0-9]/g, "");
    return `${phoneCountry}${cleaned}`;
  };

  const validate = (): string | null => {
    if (!firstName) return "First name is required";
    if (!lastName) return "Last name is required";
    // Email validation only if provided
    if (email && !/^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(email)) return "Valid email is required";
    const isNewAccount = newAccount;
    if (!isNewAccount) {
      // linking existing account: require either phone or email
      if (!phone && !email) return "Provide phone or email to link an existing account";
    } else {
      // creating new account
      if (isAdult && !phone) return "Phone is required for new adult accounts";
      if (!email) return "Email is required for new accounts";
      if (!dob) return "Date of birth is required for new accounts";
      if (!password) return "Password is required for new accounts";
      if (password !== confirmPassword) return "Passwords do not match";
      if (!termsAccepted) return "You must accept Terms & Conditions";
    }
    // Relationship validation: require at least one of Relationship (to me) or (to them)
    const missingToMe = !relationshipToMe;
    const missingToThem = !relationshipToThem;
    if (missingToMe && missingToThem) return 'Confirm Relationship to Me & Them';
    if (missingToMe) return 'Confirm Relationship to Me';
    if (missingToThem) return 'Confirm Relationship to Them';
    return null;
  };

  const handleCreate = async () => {
    setError(null);
    const v = validate();
    if (v) return setError(v);
    setLoading(true);
    try {
      // If linking to an existing account (not creating new), verify the account exists
      if (!newAccount) {
        try {
          const res: any = await apolloClient.query({
            query: CHECK_USER_AVAILABLE,
            variables: { data: { email: email || null, phone: phone ? normalizePhone(phone) : null } },
          });
          const users = res?.data?.checkUserAvailable ?? [];
          if (!users || users.length === 0) {
            setLoading(false);
            return setError('Account does not exist, check with relationship');
          }
        } catch (err: any) {
          console.error('[NewFamilyLink] checkUserAvailable error:', err);
          setLoading(false);
          return setError('Unable to verify account. Please check details with the account owner.');
        }
      }
      const input = {
        userEmail: email,
        userPhone: normalizePhone(phone) || null,
        firstName,
        lastName,
        child: child || undefined,
        isAdult: isAdult || undefined,
        // send both relationship directions: familyRelationship is "to me" (recipient),
        // familyRelationshipToThem is the reciprocal (requester). Backend expects both.
        familyRelationship: relationshipToMe || relationshipToThem || relationship || null,
        familyRelationshipToThem: relationshipToThem || relationshipToMe || relationship || null,
        DOB: dob || null,
      };
      // Note: we intentionally do not include `password` or `termsAccepted` in the GraphQL
      // input here because the server input type may not accept those fields. If the
      // backend supports setting an initial password on account creation, add it to
      // CreateFamilyRelationshipInput and include it here (e.g. password).

      const { data } = await apolloClient.mutate({
        mutation: CREATE_FAMILY_REL,
        variables: { data: input },
      });

      const result = data?.createFamilyRelationship ?? null;
      // attempt to extract a user id/name from response
      let createdId = null as string | null;
      let createdLabel = `${firstName} ${lastName}${relationshipToMe ? ` (${relationshipToMe})` : ""}`.trim();
      if (result) {
        // try common shapes
        if (result.user && result.user.id) createdId = String(result.user.id);
        else if (result.id) createdId = String(result.id);
        else if (result.createdUserId) createdId = String(result.createdUserId);
        else if (result.userId) createdId = String(result.userId);
        if (result.user && (result.user.firstName || result.user.fullName)) {
          createdLabel = `${result.user.firstName ?? result.user.fullName} ${result.user.lastName ?? ""}`.trim();
        }
      }

      setLoading(false);
      if (!createdId) createdId = `local-${Date.now()}`;

      // If linking to an existing account, show a confirmation message and close
      if (!newAccount) {
        setCreatedInfo({ id: createdId, label: createdLabel });
        // Show clearer confirmation message matching MSL wording
        setSuccessMessage(`Relationship has been requested, pending ${createdLabel} to approve`);
        // Close the modal shortly after so the user can see the confirmation text
        setTimeout(() => onClose({ id: createdId, label: createdLabel }), 800);
        return;
      }

      // If a new account was created, open verification modal(s)
      setCreatedInfo({ id: createdId, label: createdLabel });
      setShowVerificationModal(true);
    } catch (err: any) {
      console.error("createFamilyRelationship failed", err);
      setError(err?.message || "Failed to create family relationship");
      setLoading(false);
    }
  };

  return (
    <AppModal isOpen={isOpen} onClose={() => onClose()} title="Add Family Member" size="md">
      <div className="space-y-3">
        {error && <div className="text-sm text-red-700">{error}</div>}
        {successMessage && <div className="text-sm text-green-700">{successMessage}</div>}
        <div className="flex items-center justify-between">
          <div>
        <div className="text-sm text-gray-600">
        {/* <div className="text-sm text-gray-600 whitespace-nowrap"></div> */}
        Add Synkd or Clublea details for a relationship request to be sent. Child accounts, don't need mobile numbers.
             {/* <br /> */}
      </div>
            {/* <label htmlFor="newMslAccount" className="block text-sm font-medium">New A/C</label> */}
            {/* <div className="flex items-center gap-2 mt-1">
              <input
                id="newMslAccount"
                name="newMslAccount"
                type="checkbox"
                checked={newAccount}
                data-testid="new-account-checkbox"
                onChange={(e) => { const checked = e.target.checked; setNewAccount(checked); if (!checked) { setChild(false); setIsAdult(false); } }}
              />
           // <div className="text-sm text-gray-600">Input Synkd or Clublea email and it will send a request for the user to approve</div>
            </div> */}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">First name</label>
            <input data-testid="firstName" className="w-full rounded border px-2 py-1" style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium">Last name</label>
            <input data-testid="lastName" className="w-full rounded border px-2 py-1" style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Phone</label>
              <div className="flex gap-2 items-center">
                <select className="rounded border px-2 py-1" style={{ ...inputStyle, width: '64px', minWidth: '56px' }} value={phoneCountry} onChange={(e) => setPhoneCountry(e.target.value)}>
                  {PHONE_COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                {/* <input data-testid="phone" className="flex-1 rounded border px-2 py-1" style={inputStyle} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="7xxx xxxx" disabled={newAccount && child} aria-disabled={newAccount && child} /> */}
                <input data-testid="phone" className="flex-1 rounded border px-2 py-1" style={{ ...inputStyle, width: '128px', maxWidth: '62%' }} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="7xxx xxxx" disabled={newAccount && child} aria-disabled={newAccount && child} />
        
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium">Email</label>
              <input data-testid="email" className="w-full rounded border px-2 py-1" style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="relationshipToMe" className="block text-sm font-medium">Relationship (to me)</label>
            <select id="relationshipToMe" className="w-full rounded border px-2 py-1 text-sm" style={inputStyle} value={relationshipToMe} onChange={(e) => setRelationshipToMe(e.target.value)}>
              <option value="">Relationship (to me)</option>
              {FAMILY_RELATIONSHIPS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="relationshipToThem" className="block text-sm font-medium">Relationship (to them)</label>
            <select id="relationshipToThem" className="w-full rounded border px-2 py-1 text-sm" style={inputStyle} value={relationshipToThem} onChange={(e) => setRelationshipToThem(e.target.value)}>
              <option value="">Relationship (to them)</option>
              {FAMILY_RELATIONSHIPS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

      <div className="flex items-center gap-2 mt-1">
              <input
                id="newMslAccount"
                name="newMslAccount"
                type="checkbox"
                checked={newAccount}
                data-testid="new-account-checkbox"
                onChange={(e) => { const checked = e.target.checked; setNewAccount(checked); if (!checked) { setChild(false); setIsAdult(false); } }}
              />
              {/* <div className="text-sm text-gray-600">Tick to add a new Synkd or Clublea User and relationship</div> */}
              <div className="text-sm text-gray-600  whitespace-nowrap">Tick to add a new Synkd or Clublea User and relationship</div>
               {/* <br />  <br /> */}
             <br />
            </div>


        </div>


        {newAccount && (
          <>
            <div className="mt-1">
              <label className="block text-sm font-medium">Type</label>
              <div className="inline-flex rounded-md border overflow-hidden mt-2">
                <button
                  type="button"
                  className={`px-4 py-2 ${child ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => { setChild(true); setIsAdult(false); }}
                >
                  Child
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 ${isAdult ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => { setIsAdult(true); setChild(false); }}
                >
                  Adult
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium">Gender</label>
              <select className="w-full rounded border px-2 py-1" style={inputStyle} value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">Gender</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
   <div>
              <label className="block text-sm font-medium">DOB</label>
              <input data-testid="dob" type="date" className="w-full rounded border px-2 py-1" style={inputStyle} value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
            
            <div>
              <label className="block text-sm font-medium">Password</label>
              <input data-testid="password" type="password" className="w-full rounded border px-2 py-1" style={inputStyle} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium">Confirm Password</label>
              <input data-testid="confirmPassword" type="password" className="w-full rounded border px-2 py-1" style={inputStyle} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                id="agreeTerms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="h-3.5 w-3.5 flex-shrink-0 cursor-pointer"
              />
              <p className="m-0">
                I agree to{' '}
                <a
                  href={
                    isClubleaDomain()
                      // ? 'https://cdn.synkd.life/EULA%20%26%20Privacy/Clublea%20End%20User%20Licence%20Agreement%20(1.5.2026).pdf'
                      // : 'https://cdn.synkd.life/EULA%20%26%20Privacy/Synkd%20End%20User%20Licence%20Agreement%20(1.5.2025).pdf'
                      ? 'https://media-cdn.synkd.life/EULA%20%26%20Privacy/Clublea%20End%20User%20Licence%20Agreement%20(1.5.2026).pdf'
                      : 'https://media-cdn.synkd.life/EULA%20%26%20Privacy/Synkd%20End%20User%20Licence%20Agreement%20(1.5.2025).pdf'
                    }
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-blue-500 hover:underline"
                >
                  Terms &amp; Conditions
                </a>
              </p>
            </label>

         
          </>
        )}

        {/* Verification modal (simple inline dialog) */}
        {showVerificationModal && createdInfo && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded p-4 w-96">
              <h3 className="text-lg font-medium">Verify {createdInfo.label}</h3>
              <p className="text-sm text-gray-600 mt-2">Enter verification codes sent to email{isAdult ? ' and phone' : ''}.</p>
              <div className="mt-3">
                <label className="block text-sm">Email code</label>
                <input aria-label="Email code" className="w-full rounded border px-2 py-1" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} />
              </div>
              {isAdult && (
                <div className="mt-3">
                  <label className="block text-sm">Phone code</label>
                  <input aria-label="Phone code" className="w-full rounded border px-2 py-1" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} />
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button className="rounded px-4 py-2 border" onClick={() => { setShowVerificationModal(false); setCreatedInfo(null); }}>Cancel</button>
                <button className="rounded px-4 py-2 bg-purple-600 text-white" onClick={handleVerify} disabled={loading}>{loading ? 'Verifying…' : 'Verify'}</button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button className="rounded px-4 py-2 border" onClick={() => onClose()} disabled={loading}>Cancel</button>
          <button className="rounded px-4 py-2 bg-purple-600 text-white" onClick={handleCreate} disabled={loading}>{loading ? 'Creating…' : 'Link'}</button>
        </div>
      </div>
    </AppModal>
  );
}
